<template>
    <Panel size="full" padding="p-4" :corners="false">
        <div class="relative flex flex-col gap-12 tracking-[0.25px]">
            <div class="flex items-start gap-4">
                <h3
                    class="font-title flex min-w-0 flex-1 items-center gap-4 text-base leading-4 font-extrabold uppercase"
                >
                    <span class="text-titles">{{ String(rank).padStart(2, '0') }}</span>
                    <span class="truncate">{{ setup.name }}</span>
                </h3>
                <div class="flex flex-col items-end gap-1">
                    <span class="font-title text-base leading-4 font-extrabold"
                        >{{ setup.winrate }}%</span
                    >
                    <span class="text-titles text-xs leading-4 font-black uppercase">
                        {{ $t('statistics.winrate') }}
                    </span>
                </div>
            </div>

            <div
                v-if="setup.isBest || setup.is_default"
                class="absolute top-[35px] -left-px flex gap-2"
            >
                <span
                    v-if="setup.isBest"
                    class="text-bg flex h-6 items-center bg-[#00d0d0] px-2 text-xs leading-4 font-black uppercase"
                >
                    {{ $t('statistics.best_setup') }}
                </span>
                <span
                    v-if="setup.is_default"
                    class="text-bg flex h-6 items-center bg-yellow-600 px-2 text-xs leading-4 font-black uppercase"
                >
                    {{ $t('statistics.default_setup') }}
                </span>
            </div>

            <div class="flex flex-col gap-1">
                <!-- Both bars end in an 8px slant, which leaves the slanted gap between them. -->
                <div v-if="setup.games > 0" class="flex h-2">
                    <div
                        v-if="setup.wins > 0"
                        class="h-full bg-green-600 [clip-path:polygon(8px_0,100%_0,calc(100%-8px)_100%,0_100%)]"
                        :style="{ width: `${(setup.wins / setup.games) * 100}%` }"
                    ></div>
                    <div
                        v-if="setup.losses > 0"
                        class="h-full flex-1 bg-red-600 [clip-path:polygon(8px_0,100%_0,calc(100%-8px)_100%,0_100%)]"
                    ></div>
                </div>
                <div
                    v-else
                    class="bg-stroke-2 relative h-2 [clip-path:polygon(8px_0,100%_0,calc(100%-8px)_100%,0_100%)]"
                >
                    <div
                        class="bg-card absolute inset-px [clip-path:polygon(7.4px_0,100%_0,calc(100%-7.4px)_100%,0_100%)]"
                    ></div>
                </div>
                <div class="flex items-center justify-between text-sm leading-5 font-black">
                    <span :class="setup.games > 0 ? 'text-green-600' : 'text-titles'">
                        {{ $t('statistics.wins_short', { count: setup.wins }) }}
                    </span>
                    <span :class="setup.games > 0 ? 'text-red-600' : 'text-titles'">
                        {{ $t('statistics.losses_short', { count: setup.losses }) }}
                    </span>
                </div>
            </div>

            <div class="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4">
                <!-- As many of the five slots as fit, oldest to newest from the left, with "-" for
                     games not played yet. They are rendered newest first in a reversed row packed
                     to the left; slots that don't fit wrap onto a second line that the fixed
                     height hides, so the empty and oldest slots go first. -->
                <div class="flex h-5 flex-row-reverse flex-wrap justify-end gap-1 overflow-hidden">
                    <span
                        v-for="(result, index) in resultSlots"
                        :key="index"
                        class="flex size-5 items-center justify-center text-xs leading-4 font-black text-white"
                        :class="result === 'W' ? 'bg-green-600' : 'bg-titles'"
                    >
                        {{
                            result === 'W'
                                ? $t('statistics.win_letter')
                                : result === 'L'
                                  ? $t('statistics.loss_letter')
                                  : '-'
                        }}
                    </span>
                </div>
                <div class="flex gap-4 text-sm leading-5 whitespace-nowrap">
                    <span class="flex gap-1">
                        <span class="text-titles">{{ $t('statistics.avg_first_9_short') }}</span>
                        <span class="font-bold">{{ setup.avgFirstNine }}</span>
                    </span>
                    <span class="flex gap-1">
                        <span class="text-titles">{{ $t('statistics.avg_short') }}</span>
                        <span class="font-bold">{{ setup.average }}</span>
                    </span>
                </div>
            </div>
        </div>
    </Panel>
</template>

<script>
import Panel from '@/components/layout/Panel.vue'

/**
 * A dart setup's X01 record on the statistics home. `setup` is a dart setup extended with
 * games, wins, losses, winrate, average, avgFirstNine, recentResults ('W'/'L', oldest first),
 * isBest and is_default; `rank` is its place in the current sort order.
 */
export default {
    name: 'SetupStatsCard',
    components: {
        Panel,
    },
    props: {
        setup: {
            type: Object,
            required: true,
        },
        rank: {
            type: Number,
            required: true,
        },
    },
    computed: {
        /** Five slots, newest first: the recent results, then empty slots for unplayed games. */
        resultSlots() {
            const results = [...this.setup.recentResults].reverse()
            return [...results, ...Array(Math.max(5 - results.length, 0)).fill(null)]
        },
    },
}
</script>
